import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CALCULATOR_FEATURE_NAME } from '@src/constants/features';
import { useDebounceCallback } from '@src/hooks/useDebounceCallback';
import { useLoadClientOffers } from '@src/hooks/useLoadClientOffers';
import { useQueryReplacerSimple } from '@src/hooks/useQueryReplace';

import type { UpdateFullDealCalculatorPayload } from '../types';

import { updateFullDealCalculator } from './fullDeal.reducer';
import {
  selectFullDealCalculator,
  selectFullDealCalculatorBranding,
  selectFullDealCalculatorFeatures,
} from './fullDeal.selectors';

export const useSelectFullDealCalculator = () => useSelector(selectFullDealCalculator);

export const useSelectFullDealCalculatorFeatures = () => useSelector(selectFullDealCalculatorFeatures);
export const useSelectFullDealCalculatorBranding = () => useSelector(selectFullDealCalculatorBranding);

export const useUpdateCalculatorQueryFilter = () => {
  const replacer = useQueryReplacerSimple();
  const loadOffers = useLoadClientOffers();

  const { [CALCULATOR_FEATURE_NAME]: featureValue } = useSelectFullDealCalculatorFeatures();

  const updateQuery = useCallback(
    (payload: UpdateFullDealCalculatorPayload) => {
      replacer(payload);
      if (featureValue) loadOffers(payload);
    },

    // если добавить replacer
    // начнется сущий кошмар дудоса microcredits-service поэтому не надо так
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [featureValue, loadOffers],
  );

  const replaceQuery = useDebounceCallback(updateQuery, 800);

  return replaceQuery;
};

export const useUpdateFullDealCalculator = () => {
  const dispatch = useDispatch();

  return useCallback(
    (payload: UpdateFullDealCalculatorPayload, isDirty: boolean = true) => {
      dispatch(updateFullDealCalculator({ ...payload, isDirty }));
    },
    [dispatch],
  );
};

export const useUpdateCalculatorEffect = (enabled: boolean = true) => {
  const { query } = useRouter();

  const { amount, term, isDirty } = useSelectFullDealCalculator();
  const replacer = useUpdateCalculatorQueryFilter();

  const queryAmount = Number(query.amount);
  const queryTerm = Number(query.term);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if ((queryAmount === amount && queryTerm === term) || !isDirty) {
      return;
    }

    replacer({ amount, term });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, queryAmount, queryTerm, term]);
};
